import { Injectable, inject } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { OpportunityService } from '@/services/opportunity.service';
import * as OpportunityActions from './opportunity.actions';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { MessageService } from 'primeng/api';

@Injectable()
export class OpportunityEffects {
  private actions$ = inject(Actions);

  constructor(
    private opportunityService: OpportunityService,
    private messageService: MessageService
  ) {}

  // Effect to handle loading an opportunity by ID
  loadOpportunity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.loadOpportunity),
      mergeMap((action) =>
        this.opportunityService.getOpportunityById(action.id).pipe(
          map((opportunity) => OpportunityActions.loadOpportunitySuccess({ opportunity })),
          catchError((error) => of(OpportunityActions.loadOpportunityFailure({ error })))
        )
      )
    )
  );

  // Effect to handle updates to an opportunity field.
  updatedOpportunity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.updateOpportunityField),
      mergeMap((action) =>
        this.opportunityService.updateOpportunityField(action.id, action.changes).pipe(
          map((opportunity) => OpportunityActions.updateOpportunityFieldSuccess({ opportunity })),
          catchError((error) => of(OpportunityActions.updateOpportunityFieldFailure({ error })))
        )
      )
    )
  );

  // Status transition effects
  transitionToBacklog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.transitionToBacklog),
      mergeMap((action) =>
        this.opportunityService
          .transitionToBacklog(action.id, {
            assigneeId: action.assigneeId,
            backlogReasonCategoryId: action.backlogReasonCategoryId,
            backlogReason: action.backlogReason,
          })
          .pipe(
            map((opportunity) => OpportunityActions.transitionToBacklogSuccess({ opportunity })),
            catchError((error) => of(OpportunityActions.transitionToBacklogFailure({ error })))
          )
      )
    )
  );

  transitionToActive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.transitionToActive),
      mergeMap((action) =>
        this.opportunityService.transitionToActive(action.id).pipe(
          map((opportunity) => OpportunityActions.transitionToActiveSuccess({ opportunity })),
          catchError((error) => of(OpportunityActions.transitionToActiveFailure({ error })))
        )
      )
    )
  );

  transitionToInReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.transitionToInReview),
      mergeMap((action) =>
        this.opportunityService
          .transitionToInReview(action.id, {
            assigneeId: action.assigneeId,
            inReviewReasonCategoryId: action.inReviewReasonCategoryId,
            reviewReason: action.reviewReason,
          })
          .pipe(
            map((opportunity) => OpportunityActions.transitionToInReviewSuccess({ opportunity })),
            catchError((error) => of(OpportunityActions.transitionToInReviewFailure({ error })))
          )
      )
    )
  );

  transitionToDeclined$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.transitionToDeclined),
      mergeMap((action) =>
        this.opportunityService
          .transitionToDeclined(action.id, {
            declineReasonCategoryId: action.declineReasonCategoryId,
            reason: action.reason,
          })
          .pipe(
            map((opportunity) => OpportunityActions.transitionToDeclinedSuccess({ opportunity })),
            catchError((error) => of(OpportunityActions.transitionToDeclinedFailure({ error })))
          )
      )
    )
  );

  transitionToComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.transitionToComplete),
      mergeMap((action) =>
        this.opportunityService.transitionToComplete(action.id, action.reason).pipe(
          map((opportunity) => OpportunityActions.transitionToCompleteSuccess({ opportunity })),
          catchError((error) => of(OpportunityActions.transitionToCompleteFailure({ error })))
        )
      )
    )
  );

  transitionToCancelled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.transitionToCancelled),
      mergeMap((action) =>
        this.opportunityService.transitionToCancelled(action.id, action.reason).pipe(
          map((opportunity) => OpportunityActions.transitionToCancelledSuccess({ opportunity })),
          catchError((error) => of(OpportunityActions.transitionToCancelledFailure({ error })))
        )
      )
    )
  );

  // Workflow stage transition effects
  startProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.startProgress),
      mergeMap((action) =>
        this.opportunityService.startProgress(action.id).pipe(
          map((opportunity) => OpportunityActions.startProgressSuccess({ opportunity })),
          catchError((error) => of(OpportunityActions.startProgressFailure({ error })))
        )
      )
    )
  );

  requestFinalReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.requestFinalReview),
      mergeMap((action) =>
        this.opportunityService.requestFinalReview(action.id).pipe(
          map((opportunity) => OpportunityActions.requestFinalReviewSuccess({ opportunity })),
          catchError((error) => of(OpportunityActions.requestFinalReviewFailure({ error })))
        )
      )
    )
  );

  requestFinancialReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.requestFinancialReview),
      mergeMap((action) =>
        this.opportunityService.requestFinancialReview(action.id).pipe(
          map((opportunity) => OpportunityActions.requestFinancialReviewSuccess({ opportunity })),
          catchError((error) => of(OpportunityActions.requestFinancialReviewFailure({ error })))
        )
      )
    )
  );

  backToInProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.backToInProgress),
      mergeMap((action) =>
        this.opportunityService.backToInProgress(action.id).pipe(
          map((opportunity) => OpportunityActions.backToInProgressSuccess({ opportunity })),
          catchError((error) => of(OpportunityActions.backToInProgressFailure({ error })))
        )
      )
    )
  );

  cancelReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.cancelReview),
      mergeMap((action) =>
        this.opportunityService.cancelReview(action.id).pipe(
          map((opportunity) => OpportunityActions.cancelReviewSuccess({ opportunity })),
          catchError((error) => of(OpportunityActions.cancelReviewFailure({ error })))
        )
      )
    )
  );

  markDone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.markDone),
      mergeMap((action) =>
        this.opportunityService.markDone(action.id).pipe(
          map((opportunity) => OpportunityActions.markDoneSuccess({ opportunity })),
          catchError((error) => of(OpportunityActions.markDoneFailure({ error })))
        )
      )
    )
  );

  // Route for completion effect
  routeForCompletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityActions.routeForCompletion),
      mergeMap((action) =>
        this.opportunityService.routeForCompletion(action.id, action.data).pipe(
          map((opportunity) => OpportunityActions.routeForCompletionSuccess({ opportunity })),
          catchError((error) => of(OpportunityActions.routeForCompletionFailure({ error })))
        )
      )
    )
  );

  // Success notification effects
  transitionToBacklogSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpportunityActions.transitionToBacklogSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Opportunity moved to backlog successfully',
          });
        })
      ),
    { dispatch: false }
  );

  transitionToActiveSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpportunityActions.transitionToActiveSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Opportunity activated successfully',
          });
        })
      ),
    { dispatch: false }
  );

  transitionToInReviewSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpportunityActions.transitionToInReviewSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Opportunity moved to review successfully',
          });
        })
      ),
    { dispatch: false }
  );

  transitionToDeclinedSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpportunityActions.transitionToDeclinedSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Opportunity declined successfully',
          });
        })
      ),
    { dispatch: false }
  );

  transitionToCompleteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpportunityActions.transitionToCompleteSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Opportunity completed successfully',
          });
        })
      ),
    { dispatch: false }
  );

  transitionToCancelledSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpportunityActions.transitionToCancelledSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Opportunity cancelled successfully',
          });
        })
      ),
    { dispatch: false }
  );

  startProgressSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpportunityActions.startProgressSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Workflow progress started successfully',
          });
        })
      ),
    { dispatch: false }
  );

  requestFinalReviewSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpportunityActions.requestFinalReviewSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Final review requested successfully',
          });
        })
      ),
    { dispatch: false }
  );

  requestFinancialReviewSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpportunityActions.requestFinancialReviewSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Financial review requested successfully',
          });
        })
      ),
    { dispatch: false }
  );

  backToInProgressSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpportunityActions.backToInProgressSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Moved back to in-progress successfully',
          });
        })
      ),
    { dispatch: false }
  );

  markDoneSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpportunityActions.markDoneSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Opportunity marked as done successfully',
          });
        })
      ),
    { dispatch: false }
  );

  routeForCompletionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpportunityActions.routeForCompletionSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Opportunity routed for completion successfully',
          });
        })
      ),
    { dispatch: false }
  );
}
